import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  CompetenciesCompetencyGroupSelectorDocument,
  CompetenciesCompetencyGroupSelectorGroupFragment,
  CompetenciesCompetencyGroupSelectorPolicyDocument,
  CompetenciesCreateCompetencyGroupDocument,
} from 'generated/resources/graphql'
import { uniqBy } from 'lodash'

import SelectOrCreate from 'modules/core/components/SelectOrCreate'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export const CompetencyGroupSelector: React.FC<{
  value?: string
  error?: string
  onChange: (value: string | null) => void
  defaultCompetencyGroup?: CompetenciesCompetencyGroupSelectorGroupFragment | null
}> = ({ value, onChange, error, defaultCompetencyGroup }) => {
  const { company } = useCurrentSession()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const [selected, setSelected] = useState<CompetenciesCompetencyGroupSelectorGroupFragment | null>(
    null
  )

  const { data: policyData, loading: loadingPolicy } = useQuery(
    CompetenciesCompetencyGroupSelectorPolicyDocument
  )

  const {
    nodes,
    loading,
    controls: { fetchMore, hasMore, loading: loadingMore },
  } = usePaginatedQuery(CompetenciesCompetencyGroupSelectorDocument, {
    variables: { search },
    extractConnection: (data) => data.competencies.competencyGroupsConnection,
  })

  const [createCompetencyGroup, { loading: creatingCompetencyGroup }] = useMutation(
    CompetenciesCreateCompetencyGroupDocument,
    {
      extractorFn: (data) => data.competenciesMutations.createCompetenciesCompetencyGroup,
      refetchQueries: [CompetenciesCompetencyGroupSelectorDocument],
      awaitRefetchQueries: true,
    }
  )

  const competencyGroups = uniqBy(selected ? [selected, ...nodes] : nodes, (group) => group.id)

  const canCreate = Boolean(policyData?.competencies?._policies?.competencyGroupsManage.can)

  const handleChange = (ids: string | string[] | null) => {
    const id = Array.isArray(ids) ? ids[0] : ids

    if (id == null) {
      onChange(null)
      setSelected(null)
      return
    }

    const group = competencyGroups.find((g) => g.id === id)

    onChange(id)
    setSelected(group ?? null)
  }

  const handleGroupCreate = async (name: string) => {
    const response = await createCompetencyGroup({ variables: { companyId: company.id, name } })
    const result = handleResult(response, () => undefined)

    if (!result.success) return

    const createdGroup = result.data?.competencyGroup
    if (!createdGroup) return

    onChange(createdGroup.id)
    setSelected(createdGroup)
    setIsOpen(false)

    app.setFlash({
      type: 'correct',
      message: i18n.t('common.saved_successfully'),
    })
  }

  useUnsafeEffect(() => {
    if (defaultCompetencyGroup) setSelected(defaultCompetencyGroup)
  }, [defaultCompetencyGroup])

  const creationComponent = (name: string) =>
    canCreate && (
      <Box
        alignItems="center"
        justifyContent="spaceBetween"
        flexDirection="row"
        paddingX="s16"
        paddingY="s6"
      >
        <Text.Normal size="200" weight="medium">
          {name}
        </Text.Normal>
        <Button
          type="secondary"
          onClick={() => handleGroupCreate(name)}
          loading={creatingCompetencyGroup}
        >
          {i18n.t('competencies_v3.competency_group_selector.create_button')}
        </Button>
      </Box>
    )

  const placeholder = canCreate
    ? i18n.t('competencies_v3.competency_group_selector.placeholder_can_create')
    : i18n.t('competencies_v3.competency_group_selector.placeholder')

  return (
    <SelectOrCreate.Popover
      label={i18n.t('competencies_v3.competency_group_selector.label')}
      noResultsLabel={
        canCreate
          ? i18n.t('competencies_v3.competency_group_selector.blank_label_can_create')
          : i18n.t('competencies_v3.competency_group_selector.blank_label')
      }
      placeholder={placeholder}
      defaultSearch={search}
      loading={loading || loadingMore || loadingPolicy}
      isMultiSelect={false}
      inputStyle="badge"
      error={error}
      values={value ? [value] : []}
      creationComponent={creationComponent}
      isOpen={isOpen}
      fetchMore={fetchMore}
      hasMore={hasMore}
      onChange={handleChange}
      onValuesChange={handleChange}
      onToggle={setIsOpen}
      onInputChange={(input) => {
        if (value && input && !isOpen) setIsOpen(true)
        setSearch(input)
      }}
    >
      {competencyGroups.map(({ id, name }) => (
        <Selector.Option key={id} value={id} label={name} searchValue={name}>
          {name}
        </Selector.Option>
      ))}
    </SelectOrCreate.Popover>
  )
}
